import { PRESET_OPTIONS } from './periodOptions';
import { LEGACY_NAMED_PERIODS, NAMED_PERIODS, type NamedPeriod, type Period } from './types';
import { getPeriodDates } from './utils';

export interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * The persisted *intent* behind a date range. Storing the selection (rather than resolved
 * dates) lets relative presets like "Last 7 days" roll forward to today on every render.
 */
export type DateSelection =
  | { type: 'preset'; days: number; single?: boolean }
  | { type: 'named'; period: NamedPeriod }
  | { type: 'custom'; startDate: string; endDate: string }
  | { type: 'none' };

export const DEFAULT_DATE_SELECTION: DateSelection = { type: 'named', period: 'month_to_date' };
export const EMPTY_DATE_SELECTION: DateSelection = { type: 'none' };

// Resolve a selection to concrete dates using *today*, so relative presets never go stale.
export const resolveDateSelection = (selection: DateSelection): DateRange => {
  if (selection.type === 'custom') {
    return { startDate: selection.startDate, endDate: selection.endDate };
  }
  if (selection.type === 'named') {
    return getPeriodDates('', '', { title_key: '', period: selection.period });
  }
  if (selection.type === 'preset') {
    // startDate/endDate are only used by getPeriodDates for custom periods; ignored for presets.
    return getPeriodDates('', '', {
      title_key: '',
      days: selection.days,
      single: selection.single,
    });
  }
  return { startDate: '', endDate: '' };
};

// Validates the full union shape, not just the presence of `type` — localStorage is untrusted,
// so a malformed entry must fail here and fall back rather than resolve to a broken range.
const isDateSelection = (value: unknown): value is DateSelection => {
  if (typeof value !== 'object' || value === null || !('type' in value)) {
    return false;
  }
  const v = value as Record<string, unknown>;
  if (v.type === 'none') {
    return true;
  }
  if (v.type === 'custom') {
    return typeof v.startDate === 'string' && typeof v.endDate === 'string';
  }
  if (v.type === 'named') {
    return (NAMED_PERIODS as readonly string[]).includes(v.period as string);
  }
  if (v.type === 'preset') {
    return typeof v.days === 'number';
  }
  return false;
};

// Runs before validation, so `isDateSelection` stays strict about current names.
const canonicalizeNamedPeriod = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) {
    return value;
  }
  const v = value as Record<string, unknown>;
  if (v.type !== 'named' || typeof v.period !== 'string') {
    return value;
  }
  const canonical = LEGACY_NAMED_PERIODS[v.period];
  return canonical ? { ...v, period: canonical } : value;
};

export const parseDateSelection = (value: unknown): DateSelection | undefined => {
  const candidate = canonicalizeNamedPeriod(value);
  return isDateSelection(candidate) ? candidate : undefined;
};

/**
 * Migrates legacy persisted values. A typeless `{startDate, endDate}` becomes `custom`, since its
 * original intent is unrecoverable; renamed period keys are rewritten.
 */
export const normalizeSelection = (value: unknown, fallback: DateSelection): DateSelection => {
  const parsed = parseDateSelection(value);
  if (parsed) {
    return parsed;
  }
  if (value && typeof value === 'object' && 'startDate' in value && 'endDate' in value) {
    const { startDate, endDate } = value as Record<string, unknown>;
    if (typeof startDate === 'string' && typeof endDate === 'string' && startDate && endDate) {
      return { type: 'custom', startDate, endDate };
    }
  }
  return fallback;
};

export const periodToSelection = (period: Period): DateSelection | null => {
  if (period.period) {
    return { type: 'named', period: period.period };
  }
  if (period.days !== undefined) {
    return { type: 'preset', days: period.days, single: period.single };
  }
  return null;
};

export const selectionToPeriod = (selection?: DateSelection): Period | null | undefined => {
  if (!selection) {
    return undefined;
  }
  if (selection.type === 'named') {
    return PRESET_OPTIONS.find((option) => option.period === selection.period) ?? null;
  }
  if (selection.type === 'preset') {
    return (
      PRESET_OPTIONS.find(
        (option) => option.days === selection.days && !!option.single === !!selection.single
      ) ?? null
    );
  }
  return null;
};
